<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="(v) => $emit('update:modelValue', v)">
    <q-card style="min-width: 380px; max-width: 520px" data-test="dialog-gerar-link">
      <q-card-section class="row items-center no-wrap">
        <div class="text-h6">Gerar link de pagamento</div>
        <q-space />
        <q-btn icon="close" flat round dense data-test="fechar-gerar-link" :disable="Boolean(enviando)" @click="$emit('update:modelValue', false)" />
      </q-card-section>

      <q-card-section v-if="conferindo" class="text-center">
        <q-spinner color="primary" size="2em" />
      </q-card-section>

      <template v-else-if="previa">
        <q-card-section class="q-pt-none">
          <template v-if="temTaxa">
            <div class="row items-center q-mb-xs">
              <div class="col">Valor da venda</div>
              <div class="col-auto" data-test="previa-valor-venda">R$ {{ $currency(valorVenda) }}</div>
            </div>
            <div class="q-mb-sm" data-test="previa-taxa">
              <div class="text-caption text-grey-7">Taxa de conveniência</div>
              <div v-for="meio in meios" :key="meio" class="row items-center" :data-test="`previa-taxa-${meio}`">
                <div class="col">{{ MEIO_NOMES[meio] }}: taxa R$ {{ $currency(taxaDoMeio(meio)) }}</div>
                <div class="col-auto">total R$ {{ $currency(totalDoMeio(meio)) }}</div>
              </div>
              <template v-if="taxa.editavel">
                <campo-valor
                  :key="campoTaxaKey"
                  v-model="taxaEditada"
                  label="Alterar taxa"
                  data-test="previa-taxa-editada"
                  :error="!!erroDaTaxa"
                  :error-message="erroDaTaxa"
                />
                <div class="row items-center">
                  <div class="col text-caption text-grey-7">R$ 0,00 deixa a venda sem taxa.</div>
                  <q-btn
                    v-if="taxaFoiEditada"
                    flat
                    dense
                    no-caps
                    color="primary"
                    label="Usar taxa da faixa"
                    data-test="usar-taxa-da-faixa"
                    @click="resetTaxa"
                  />
                </div>
              </template>
              <div v-else-if="taxa.modo === 'editavel'" class="text-caption text-grey-7" data-test="previa-taxa-sem-edicao">{{ avisoDaTaxaFixa }}</div>
            </div>
          </template>
          <div class="row items-center q-mb-sm">
            <div class="col">Total</div>
            <div class="col-auto text-h6" data-test="previa-total">R$ {{ $currency(totalDoMeio(meios[0])) }}</div>
          </div>
          <div v-if="previa.valor_maximo" class="row items-center q-mb-sm text-grey-8" data-test="previa-limite">
            <div class="col">Limite por venda</div>
            <div class="col-auto">R$ {{ $currency(previa.valor_maximo) }}</div>
          </div>
          <div class="row items-center">
            <div class="col">O link vale até</div>
            <div class="col-auto text-weight-bold" data-test="previa-corte">{{ formatMomento(previa.corte_em) || "—" }}</div>
          </div>
        </q-card-section>

        <q-card-section v-if="itens.length" class="q-pt-none">
          <q-list dense bordered separator>
            <q-item v-for="(item, i) in itens" :key="i" data-test="previa-item">
              <q-item-section>
                <q-item-label>{{ item.quantidade }}× {{ item.produto_nome }}</q-item-label>
                <q-item-label v-if="item.disponivel" caption>vale até {{ formatMomento(item.corte_em) }}</q-item-label>
                <q-item-label v-else caption class="text-negative" data-test="previa-item-recusa">{{ item.motivo }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-icon :name="item.disponivel ? 'check_circle' : 'block'" :color="item.disponivel ? 'positive' : 'negative'" />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-section v-if="generalPendencias.length" class="q-pt-none">
          <q-banner dense class="bg-orange-1 text-orange-10" data-test="previa-pendencias">
            <div class="text-weight-bold q-mb-xs">Resolva antes de gerar:</div>
            <ul class="q-my-none q-pl-md">
              <li v-for="(pendencia, i) in generalPendencias" :key="i" data-test="previa-pendencia">{{ pendencia.mensagem }}</li>
            </ul>
            <template v-if="hasPendenciaDoCliente" #action>
              <q-btn flat dense no-caps color="orange-10" label="Conferir de novo" data-test="conferir-de-novo" @click="check" />
            </template>
          </q-banner>
        </q-card-section>
      </template>

      <q-card-section v-else-if="erro" class="q-pt-none text-negative" data-test="previa-erro">{{ erro }}</q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-for="(meio, ordem) in meios"
          :key="meio"
          no-caps
          :unelevated="ordem === 0"
          :outline="ordem > 0"
          color="primary"
          :icon="MEIO_ICONES[meio]"
          :label="`Gerar ${MEIO_NOMES[meio]}`"
          :data-test="`gerar-${meio}`"
          :loading="enviando === meio"
          :disable="!canConfirm"
          @click="confirm(meio)"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { getRascunhoChave, forgetRascunhoChave, formatMomento } from "@/utils/venda-online";
import CampoValor from "@/components/VendasOnline/campo-valor.vue";

const MEIO_NOMES = { pix: "Pix", boleto_pix: "Boleto/Pix" };

const MEIO_ICONES = { pix: "qr_code_2", boleto_pix: "receipt_long" };

const PENDENCIAS_DO_CLIENTE = ["cliente_id", "cliente_nome", "cliente_cpf", "cliente_contato", "cliente_email", "cliente_endereco"];

export default {
  name: "DialogGerarLink",
  components: { CampoValor },
  props: {
    modelValue: { type: Boolean, default: false },
    pedido: { type: Object, required: true }
  },
  emits: ["update:modelValue", "gerada"],
  data() {
    return {
      previa: null,
      pedidoConferido: null,
      versaoPrevia: 0,
      pendencias: [],
      erro: null,
      conferindo: false,
      enviando: null,
      taxaEditada: null,
      campoTaxaKey: 0,
      MEIO_NOMES,
      MEIO_ICONES
    };
  },
  computed: {
    itens() {
      return (this.previa && this.previa.itens) || [];
    },
    generalPendencias() {
      const recusas = this.itens.filter((item) => !item.disponivel).map((item) => item.motivo);

      return this.pendencias.filter((pendencia) => !(pendencia.item === "itens" && recusas.includes(pendencia.mensagem)));
    },
    meios() {
      const meios = ((this.previa && this.previa.meios) || []).filter((meio) => MEIO_NOMES[meio]);

      return meios.length ? meios : ["pix"];
    },
    hasPendenciaDoCliente() {
      return this.pendencias.some((pendencia) => PENDENCIAS_DO_CLIENTE.includes(pendencia.item));
    },
    taxa() {
      return (this.previa && this.previa.taxa_conveniencia) || null;
    },
    temTaxa() {
      return Boolean(this.taxa) && (this.taxa.editavel || this.meios.some((meio) => (this.taxa.por_meio || {})[meio] > 0));
    },
    valorVenda() {
      return this.previa.valor_venda ?? this.previa.valor_total;
    },
    taxaFoiEditada() {
      return Boolean(this.taxa && this.taxa.editavel) && this.taxaEditada !== null;
    },
    erroDaTaxa() {
      return this.taxaFoiEditada && Math.round(this.taxaEditada * 100) > Math.round(this.valorVenda * 100) ? "A taxa não pode passar do valor da venda." : "";
    },
    avisoDaTaxaFixa() {
      const permissao = "Só quem tem a permissão Alterar taxa de conveniência pode mudar a taxa";

      return this.taxa.limite > 0 ? `${permissao}, em vendas acima de R$ ${this.$currency(this.taxa.limite)}.` : `${permissao}.`;
    },
    canConfirm() {
      return Boolean(this.previa) && this.pendencias.length === 0 && !this.erroDaTaxa && !this.conferindo && !this.enviando;
    }
  },
  watch: {
    taxaEditada() {
      this.pendencias = this.pendencias.filter((pendencia) => pendencia.item !== "taxa_conveniencia");
    },
    modelValue: {
      immediate: true,
      handler(aberto) {
        if (aberto) this.check();
      }
    },
    pedido(novo) {
      if (this.modelValue && JSON.stringify(novo) !== JSON.stringify(this.pedidoConferido)) this.check();
    }
  },
  methods: {
    formatMomento,
    resetTaxa() {
      this.taxaEditada = null;
      this.campoTaxaKey += 1;
    },
    taxaDoMeio(meio) {
      if (this.taxaFoiEditada) return this.taxaEditada;

      return ((this.taxa && this.taxa.por_meio) || {})[meio] || 0;
    },
    totalDoMeio(meio) {
      if (!this.taxa) return this.previa.valor_total;

      return Math.round((this.valorVenda + this.taxaDoMeio(meio)) * 100) / 100;
    },
    async check() {
      this.previa = null;
      this.taxaEditada = null;
      this.pendencias = [];
      this.erro = null;
      this.conferindo = true;

      const versao = ++this.versaoPrevia;
      this.pedidoConferido = this.pedido;
      const resultado = await this.$store.dispatch("previewVendaOnline", this.pedido);
      if (versao !== this.versaoPrevia) return;
      this.conferindo = false;

      if (!resultado.ok) {
        this.erro = resultado.mensagem;
        return;
      }

      this.previa = resultado.previa;
      this.pendencias = resultado.previa.pendencias || [];
    },
    async confirm(meio) {
      if (!this.canConfirm) return;
      this.enviando = meio;

      const pedido = { ...this.pedidoConferido, meio, ...(this.taxaFoiEditada ? { taxa_conveniencia: this.taxaEditada } : {}) };
      const resultado = await this.$store.dispatch("createVendaOnline", {
        ...pedido,
        chave_idempotencia: getRascunhoChave(pedido)
      });
      this.enviando = null;

      if (!resultado.ok) {
        this.pendencias = resultado.pendencias;
        this.$q.notify({ message: resultado.mensagem, color: "negative", position: "top-right" });
        return;
      }

      forgetRascunhoChave(pedido);
      this.$emit("gerada", resultado.venda);
      this.$emit("update:modelValue", false);
    }
  }
};
</script>
